/* =========================================================
   Recharge Bundle Revamp — V2 (RBX2)
   Presentational re-skin: RENDER FROM NATIVE TRUTH, PROXY all
   interactions back to ReCharge's own controls so cart /
   subscription logic stays with ReCharge.

   Supports both ReCharge bundle DOM layouts:
     • Sectioned  : .rb-section + .rb-section-title + .rb-remainder
     • Box-builder: .rb-items-wrapper > .rb-item (no sections)
   See memory: recharge-bundle-dom-variants.
========================================================= */

(() => {
  const CTRL = document.querySelector('[data-rbx2-controller][data-rbx-design="v2"]');
  if (!CTRL) return; // never runs on classic pages

  const ROOT = document.querySelector("#recharge-bundles");
  if (!ROOT) { console.warn("[RBX2] #recharge-bundles not found"); return; }

  const qs  = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => Array.from(c.querySelectorAll(s));
  const esc = (s) => { const d = document.createElement("div"); d.textContent = s == null ? "" : String(s); return d.innerHTML; };
  const num = (s) => { const n = parseInt(String(s ?? "").replace(/\D/g, ""), 10); return isNaN(n) ? 0 : n; };
  const pad2 = (n) => String(n).padStart(2, "0");
  // same view test as the original sticky bar
  const isInView = (el, threshold = 0.35) => {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return (Math.min(r.bottom, vh) - Math.max(r.top, 0)) / Math.max(r.height, 1) >= threshold;
  };

  // ---------- Config ----------
  function getCFG() {
    const el = qs("[data-rbx2-json]", CTRL);
    if (!el) return {};
    try {
      return JSON.parse((el.textContent || "").trim().replace(/"img"\s*:\s*Liquid error[^,}\]]*/g, '"img":""'));
    } catch (e) { console.error("[RBX2] bad JSON", e); return {}; }
  }
  const CFG   = getCFG();
  const COPY  = CFG.copy  || {};
  const STYLE = CFG.style || {};
  const GIFTS = Array.isArray(CFG.gifts) ? CFG.gifts.filter(g => g && (g.month || g.text || g.img)) : [];
  if (CFG.enabled === false) { console.log("[RBX2] disabled via setting"); return; }

  const CHECK = '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="8" cy="8" r="8" fill="currentColor"/><path d="M4.5 8.2l2.2 2.2 4.8-4.8" stroke="#fff" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const CARET = '<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 8l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // ---------- Widget-type detection: old ".rb-*" vs new "recharge-storefront-theme" ----------
  // OPT-IN per template (CFG.newWidget): existing bundles that use the old .rb-* widget
  // (e.g. new-sachet-bundle, new-starter-bundle) never enable this, so they always take
  // the original code path and are guaranteed unaffected.
  function isNewWidget() {
    if (CFG.newWidget !== true) return false;
    return !qs(".rb-item", ROOT) && !!qs(".recharge-bundle-product-item, .recharge-storefront-theme", ROOT);
  }
  // Newer widget keeps the selling plan in a hidden, React-controlled <select>.
  function newSelect() { return qs('[data-testid="hidden-select-container"] select', ROOT); }
  // React-controlled <select>/<input>: set value via the native setter so React's
  // internal value tracker notices the change, then fire bubbling input+change events.
  function setNativeValue(el, value) {
    const proto = el.tagName === "SELECT" ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, "value") && Object.getOwnPropertyDescriptor(proto, "value").set;
    if (setter) setter.call(el, value); else el.value = value;
    el.dispatchEvent(new Event("input", { bubbles: true }));
    el.dispatchEvent(new Event("change", { bubbles: true }));
  }
  // NOTE: the button label is split across <div>Add</div><div>to</div><div>cart:</div>,
  // so textContent is "Addtocart:" with no spaces — match with optional whitespace.
  const ATC_RE = /add\s*to\s*cart/i;
  function newAtcBtns() { return qsa("button", ROOT).filter(b => ATC_RE.test(b.textContent || "")); }
  function newAtcBtn() {
    const desk = qs('[data-testid="bundle-summary-desktop"]', ROOT);
    return (desk && qsa("button", desk).find(b => ATC_RE.test(b.textContent || ""))) || newAtcBtns()[0];
  }
  function newQuota() {
    const t = qs(".recharge-bundle-variant-size-title", ROOT) && qs(".recharge-bundle-variant-size-title", ROOT).textContent || "";
    const m = t.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  }
  // Bundle price lives in the ATC button: "Add to cart: <s>£104.99</s> £44.94"
  // (where £104.99 is the compare/was and £44.94 the actual/now).
  function newBundlePrice() {
    const btn = newAtcBtn();
    if (!btn) return { now: "", was: "" };
    // Prefer the dedicated price span (has aria-label "...Actual price..."); fall back
    // to the element wrapping the <s>, then to the whole button.
    const sEl = qs("s", btn);
    const span = qs('[aria-label*="price" i]', btn) || (sEl && sEl.parentElement) || btn;
    const was = sEl ? sEl.textContent.trim() : "";
    // Pull money tokens out of the visible text — ignores the "Addtocart:" label noise.
    const monies = (span.textContent || "").match(/[£$€]\s?[\d.,]+/g) || [];
    let now = was ? (monies.find(x => x.trim() !== was) || monies[monies.length - 1] || "")
                  : (monies[monies.length - 1] || "");
    return { now: normMoney(now.trim()), was: normMoney(was) };
  }
  // Out-of-stock detection. SAFE DEFAULT: explicit sold-out / out-of-stock text in the
  // native ReCharge item — never false-positives on a quota lock. If the widget flags OOS
  // some other way (a class/attribute), add that check here.
  function isOOS(el) {
    if (!el) return false;
    var OOS_RE = /\b(sold\s*out|out\s*of\s*stock|no longer available|unavailable)\b/i;
    // Definitive signal (new ReCharge widget): a button whose aria-label / text is "Sold out".
    // OOS-specific — an in-stock item that's merely quota-locked keeps its "Add" label.
    var btns = el.querySelectorAll('button');
    for (var i = 0; i < btns.length; i++) {
      if (OOS_RE.test((btns[i].getAttribute("aria-label") || "") + " " + (btns[i].textContent || ""))) return true;
    }
    // Fallbacks (old widget / class-based markup)
    if (/(out[-_ ]?of[-_ ]?stock|sold[-_ ]?out|unavailable|\boos\b)/i.test(el.className || "")) return true;
    return OOS_RE.test(el.textContent || "");
  }

  function readItemNew(el) {
    const id = el.getAttribute("data-recharge-variant-id") || el.getAttribute("data-recharge-product-id") || "";
    const titleEl = qs('[role="heading"][aria-level="3"]', el) || qs(".recharge-heading-h3", el);
    var _cim = CFG.cardImages || {};
    var img = _cim[el.getAttribute("data-recharge-variant-id")] || _cim[el.getAttribute("data-recharge-product-id")] || (qs("img", el) && qs("img", el).getAttribute("src")) || "";
    const input = qs('input[type="number"]', el);
    const qty = input ? num(input.value) : 0;
    const inc = qs('[data-testid="increment"]', el);
    const dec = qs('[data-testid="decrement"]', el);
    const addBtn = qsa("button", el).find(b => /^\s*add\s*$/i.test(b.textContent || "")) || qs(".recharge-button-primary", el);
    return {
      el, id,
      title: (titleEl && titleEl.textContent.trim()) || "Item",
      img, was: "", now: "",
      qty,
      plus: qty > 0 ? inc : addBtn,   // qty 0 → "Add" button; qty>0 → stepper "+"
      minus: dec,
      locked: false,
      oos: isOOS(el),
    };
  }

  // ---------- Wait for native widget (supports old .rb-* AND new recharge-* layouts) ----------
  function waitForWidget(cb) {
    let tries = 0;
    const t = setInterval(() => {
      const oldReady = qs(".rb-box-builder", ROOT) && qsa(".rb-item", ROOT).length && qs("#selling-plan-list", ROOT);
      const newReady = qsa(".recharge-bundle-product-item", ROOT).length && newSelect();
      if (oldReady || newReady) {
        clearInterval(t);
        console.log("[RBX2] widget ready —", isNewWidget() ? "NEW (recharge-storefront-theme)" : "OLD (.rb-*)");
        cb();
      } else if (++tries > 200) {
        clearInterval(t);
        console.warn("[RBX2] timed out waiting for ReCharge widget");
      }
    }, 200);
  }

  // ---------- Native readers ----------
  function globalQuota() {
    const portal = qsa(".rb-portal-heading, .rb-notice", ROOT).map(e => e.textContent || "").join(" ");
    let m = portal.match(/at least\s+(\d+)/i) || portal.match(/Choose\s+(\d+)/i);
    if (m) return parseInt(m[1], 10);
    const left = qsa("[data-rb-left]", ROOT).map(e => num(e.getAttribute("data-rb-left"))).filter(Boolean);
    return left.length ? Math.max(...left) : 0;
  }

  function readItem(el) {
    const input = qs(".rb-input", el);
    const priceEl = qs(".rb-price", el);
    const was = qs(".rb-full-price", priceEl || el)?.textContent.trim() || "";
    let now = "";
    if (priceEl) now = (priceEl.textContent || "").replace(was, "").replace(/\s+/g, " ").trim();
    return {
      el,
      id: el.getAttribute("data-rb-item-id") || "",
      title: qs(".rb-title span", el)?.textContent.trim() || qs(".rb-title", el)?.textContent.trim() || "Item",
      img: (CFG.cardImages || {})[el.getAttribute("data-rb-item-id")] || (qs(".rb-item-img img", el)?.getAttribute("src") || ""),
      was, now,
      qty: num(input?.value),
      plus:  qs(".rb-btn-qty-plus", el),
      minus: qs(".rb-btn-qty-minus", el),
      locked: el.getAttribute("aria-disabled") === "true",
      oos: isOOS(el),
    };
  }

  function getSelect() { return isNewWidget() ? newSelect() : qs("#selling-plan-list", ROOT); }
  function planOptions() {
    const sel = getSelect();
    if (!sel) return [];
    const isNew = isNewWidget();
    return qsa("option", sel).map((o) => {
      const id = isNew ? (o.value || "0") : (o.getAttribute("data-selling-plan-id") || "0");
      const label = (o.textContent || "").trim();
      return { el: o, id, label, isOneTime: id === "0" || /only once|one time only/i.test(label) };
    }).filter(o => o.label);   // drop the empty placeholder <option> the new widget renders
  }
  function currentPlanId() {
    const sel = getSelect();
    if (!sel) return "0";
    if (isNewWidget()) {
      if (sel.value) return sel.value;
      const opt = qs("option:checked", sel);
      return opt ? (opt.value || "0") : "0";
    }
    return qs("option:checked", sel)?.getAttribute("data-selling-plan-id")
        || sel?.options[sel.selectedIndex]?.getAttribute("data-selling-plan-id") || "0";
  }
  // Resolve the configured default plan (by id, or case-insensitive label match e.g. "2 month")
  function defaultPlanId() {
    const want = String(COPY.defaultPlan || "").trim();
    if (!want) return "";
    const opts = planOptions();
    let o = opts.find(p => p.id === want);
    if (o) return o.id;
    o = opts.find(p => p.label.toLowerCase().indexOf(want.toLowerCase()) !== -1);
    return o ? o.id : "";
  }
  function applyDefaultPlan() {
    const id = defaultPlanId();
    if (id && !_userPicked && currentPlanId() !== id) setPlanById(id);
  }

  function readModel() {
    let steps;
    if (isNewWidget()) {
      // New widget: one quota ("Choose N") across collections → a single step.
      const items = qsa(".recharge-bundle-product-item", ROOT).map(readItemNew);
      const need = newQuota();
      const title = (COPY.sectionTitle && COPY.sectionTitle.trim())
        || (need ? `Choose ${need}` : "Choose your items");
      steps = [{ el: ROOT, title, need, items }];
    } else {
      const sections = qsa(".rb-section", ROOT);
      if (sections.length) {
        steps = sections.map((s) => ({
          el: s,
          title: qs(".rb-section-title", s)?.textContent.trim() || "Step",
          need: num((qs(".rb-remainder", s)?.textContent.match(/Choose\s+(\d+)/i) || [])[1]),
          items: qsa(".rb-item", s).map(readItem),
        }));
      } else {
        const items = qsa(".rb-item", qs(".rb-items-wrapper", ROOT) || ROOT).map(readItem);
        const need = globalQuota();
        const title = (COPY.sectionTitle && COPY.sectionTitle.trim())
          || (need ? `Choose ${need}` : "Choose your items");
        steps = [{ el: ROOT, title, need, items }];
      }
    }
    // hard cap: override the single-step quota with the configured max (if set)
    const maxSel = parseInt(COPY.maxSelect, 10);
    if (maxSel > 0 && steps.length === 1) { steps[0].need = maxSel; }
    steps.forEach((st) => { st.chosen = st.items.reduce((n, i) => n + i.qty, 0); st.complete = !st.need || st.chosen >= st.need; });

    const opts = planOptions();
    const subOpts = opts.filter(o => !o.isOneTime);
    const oneTime = opts.find(o => o.isOneTime);
    const curId = currentPlanId();
    const isSub = curId !== "0" && !!subOpts.find(o => o.id === curId);

    const discMatch = ((subOpts.find(o => o.id === curId) || subOpts[0])?.label || COPY.badgeLabel || "").match(/(\d+)\s*%/);
    const discount = discMatch ? discMatch[1] : "";

    const price = bundlePrice();   // bundle-level price (works for single- and multi-step)

    const atcBtns = isNewWidget()
      ? newAtcBtns()
      : qsa(".rb-btn", ROOT).filter(b => /add to cart/i.test(b.textContent || ""));
    const atcDisabled = atcBtns.length ? atcBtns.every(b => b.disabled) : true;

    return {
      steps, opts, subOpts, oneTime, isSub, curId, discount, price, atcBtns, atcDisabled,
      totalNeed: steps.reduce((n, s) => n + (s.need || 0), 0),
      totalChosen: steps.reduce((n, s) => n + s.chosen, 0),
      allComplete: steps.every(s => s.complete),
    };
  }

  // ---------- Proxy actions ----------
  const fire = (el) => { if (el && !el.disabled) el.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window })); };
  // Same as fire(), but neutralises any <form> submit the synthetic click would trigger.
  // ReCharge's quantity/Add buttons can render as <button> with no explicit type (→ type
  // "submit"); on iOS Safari a synthetic click then submits the enclosing form and the
  // product page reloads. React's own onClick (the real quantity update) still runs — we
  // only suppress the browser's submit/navigation for the duration of THIS dispatch.
  function fireQty(el) {
    if (!el || el.disabled) return;
    const form = el.form || (el.closest && el.closest("form"));
    const block = (ev) => ev.preventDefault();
    if (form) form.addEventListener("submit", block, true);
    try {
      el.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
    } finally {
      if (form) form.removeEventListener("submit", block, true);
    }
  }
  function setPlanById(id) {
    const sel = getSelect(); if (!sel) return;
    if (isNewWidget()) {
      const opt = qsa("option", sel).find(o => (o.value || "0") === String(id));
      if (!opt) return;
      setNativeValue(sel, opt.value || "0");              // React-controlled select
      return;
    }
    const opt = qsa("option", sel).find(o => (o.getAttribute("data-selling-plan-id") || "0") === String(id));
    if (!opt) return;
    opt.selected = true;                                   // <-- correct: select by option, not value
    sel.dispatchEvent(new Event("change", { bubbles: true }));
  }
  function fireAtc() { const m = readModel(); fire(m.atcBtns.find(b => !b.disabled) || m.atcBtns[0]); }

  // ---------- State ----------
  let activeStep = 0;
  let giftsOpen = true;   // gifts box expanded by default (per design)
  let _freeze = false;    // pause re-render while the delivery dropdown is open
  let _probing = false;   // pause re-render while probing both plan prices
  let _userPicked = false; // true once the shopper chooses a frequency (stops default auto-select)
  let _lastSig = "";      // last item-quantity signature we probed for
  const priceCache = { otp: null, sub: null };  // { now, was } per plan

  // Read the bundle price the native widget currently shows.
  // Mirrors the original parsePrices(): the FIRST .rb-price / .rb-full-price in ROOT,
  // so it works whether price is per-item (single-step) or a bundle total (multi-step).
  function bundlePrice() {
    if (isNewWidget()) return newBundlePrice();
    const priceEl = qs(".rb-price", ROOT);
    if (!priceEl) return { now: "", was: "" };
    const was = (qs(".rb-full-price", priceEl) || qs(".rb-full-price", ROOT))?.textContent.trim() || "";
    const now = (priceEl.textContent || "").replace(was, "").replace(/\s+/g, " ").trim();
    return { now: normMoney(now), was: normMoney(was) };
  }

  // Numeric value out of a money string ("£104.97" -> 104.97); strips symbol + thousands separators
  const moneyNum = (s) => parseFloat(String(s || "").replace(/[^\d.]/g, ""));
  // Currency symbol/prefix from a money string ("£104.97" -> "£", "$" etc.)
  const moneySym = (s) => (String(s || "").match(/[^\d.,\s-]+/) || [""])[0];
  // Strip country-code prefix/suffix ("US$45.00" -> "$45.00", "$45.00 USD" -> "$45.00")
  const normMoney = (s) => String(s || "").replace(/[A-Z]{2,3}\s*(?=\$)/g, "").replace(/(?<=\$[\d.,]+\s*)[A-Z]{2,3}/g, "").trim();
  // "Save £49.86" style amount in the SAME currency as the source strings
  function saveAmount(wasStr, nowStr) {
    const w = moneyNum(wasStr), n = moneyNum(nowStr);
    if (!isFinite(w) || !isFinite(n) || w <= n) return "";
    return moneySym(wasStr) + (w - n).toFixed(2);
  }
  // Clean frequency label: "1 month subscription - 30% discount" -> "1 month"
  const freqLabel = (s) => {
    let t = String(s || "").trim();
    if (isNewWidget()) t = t.replace(/\s*:.*$/, "");   // new widget: drop ": save 42.9%" → "Every 2 months"
    t = t.replace(/\s*subscription\b.*$/i, "");   // drop "subscription …"
    t = t.replace(/\s*[-–—]\s*\d+\s*%.*$/i, "");   // drop "- 30% discount" if no "subscription"
    t = t.replace(/\s*\(.*\)\s*$/, "");            // drop trailing "(…)"
    return t.trim() || String(s || "").trim();
  };

  // Subscription discount as a % computed from probed prices ((one-time − sub) / one-time)
  function subPercent(m) {
    const o = moneyNum(priceCache.otp && priceCache.otp.now);
    const s = moneyNum(priceCache.sub && priceCache.sub.now);
    if (o && s && o > s) return Math.round((o - s) / o * 100);
    return m.discount || "";
  }

  // Briefly flip the (hidden) native select to read BOTH plan prices, then restore.
  // Render is frozen during the probe so the user never sees the flips.
  function probePrices() {
    if (_probing) return;
    const sel = getSelect(); if (!sel) return;
    const opts = planOptions();
    const sub = opts.find(o => !o.isOneTime);
    const one = opts.find(o => o.isOneTime);
    if (!sub || !one) return;

    _probing = true;
    const isNew = isNewWidget();
    const prevId = currentPlanId();
    const pick = (o) => {
      if (isNew) setNativeValue(sel, o.value || "0");
      else { o.selected = true; sel.dispatchEvent(new Event("change", { bubbles: true })); }
    };
    const optById = (id) => qsa("option", sel).find(o =>
      (isNew ? (o.value || "0") : (o.getAttribute("data-selling-plan-id") || "0")) === String(id));

    pick(one.el);
    setTimeout(() => {
      priceCache.otp = bundlePrice();
      pick(sub.el);
      setTimeout(() => {
        priceCache.sub = bundlePrice();
        pick(optById(prevId) || sub.el);            // restore the user's plan
        setTimeout(() => { _probing = false; render(); }, 140);
      }, 170);
    }, 170);
  }

  // ---------- Token override ----------
  function applyTokens(node) {
    const map = { bg:"--rbx2-bg", text:"--rbx2-text", muted:"--rbx2-muted", border:"--rbx2-line", radius:"--rbx2-radius" };
    Object.entries(map).forEach(([k, v]) => {
      if (STYLE[k] != null && STYLE[k] !== "") node.style.setProperty(v, k === "radius" ? STYLE[k] + "px" : STYLE[k]);
    });
  }

  // ---------- Builders ----------
  function stepTabs(m) {
    const word = esc(COPY.stepWord || "STEP");
    if (m.steps.length <= 1) return "";   // single-step bundle → no step tabs
    return `<div class="rbx2__tabs">` + m.steps.map((s, i) =>
      `<button type="button" class="rbx2__tab ${i === activeStep ? "is-active" : ""} ${(i > 0 && !m.steps[i-1].complete) ? "is-disabled" : ""}" data-rbx2-step="${i}">${word} ${pad2(i+1)}</button>`
    ).join("") + `</div>`;
  }

  function card(it, stepFull, sub) {
    const meta = `<div class="rbx2__card-meta">${sub ? `<span class="rbx2__c-sub">${esc(sub)}</span>` : ""}${it.was ? `<span class="rbx2__c-was">${esc(it.was)}</span>` : ""}${it.now ? `<span class="rbx2__c-now">${esc(it.now)}</span>` : ""}</div>`;
    const lockPlus = stepFull ? "disabled" : "";   // can't add more once the quota is met
    const action = it.oos
      ? `<button type="button" class="rbx2__add rbx2__sold-out" disabled aria-disabled="true">${esc(COPY.soldOutLabel || "Sold Out")}</button>`
      : it.qty > 0
      ? `<div class="rbx2__stepper">
           <button type="button" class="rbx2__sbtn minus" data-rbx2-minus aria-label="Decrease">–</button>
           <span class="rbx2__sval">${pad2(it.qty)}</span>
           <button type="button" class="rbx2__sbtn plus" data-rbx2-plus aria-label="Increase" ${lockPlus}>+</button>
         </div>`
      : `<button type="button" class="rbx2__add" data-rbx2-plus ${lockPlus}>${esc(COPY.addLabel || "+Add")}</button>`;
    return `<div class="rbx2__card ${it.qty > 0 ? "is-selected" : ""} ${it.locked ? "is-locked" : ""} ${it.oos ? "is-oos" : ""}" data-rbx2-item="${esc(it.id)}">
      <div class="rbx2__card-img"${it.img ? ` data-img-src="${esc(it.img)}" data-img-alt="${esc(it.title)}"` : ""}></div>
      <div class="rbx2__card-body">
        <div class="rbx2__card-info">
          <p class="rbx2__card-title">${esc(it.title)}</p>
          ${(sub || it.was || it.now) ? meta : ""}
        </div>
        <div class="rbx2__card-action">${action}</div>
      </div>
    </div>`;
  }

  function giftsBox(m) {
    if (!GIFTS.length) return "";
    const monthly = (COPY.giftsMonthly || "").toString().replace(/[^\d]/g, "");
    const title = (COPY.benefitsTitle && /includes/i.test(COPY.benefitsTitle)) ? COPY.benefitsTitle.replace(/:\s*$/, "") : "Subscription includes";
    const additional = (COPY.giftsAdditional || "").toString().replace(/[^\d]/g, "");
    const monthlyBadge = monthly ? `<span class="rbx2__badge">-${esc(monthly)}%</span>` : "";
    const additionalBadge = additional ? `<span class="rbx2__badge">-${esc(additional)}%</span>` : "";
    // Header line is editable: hide via toggle, or drop a clause by blanking its % setting
    const showHeader = COPY.giftsShowHeader !== false;
    const customHeader = (COPY.giftsHeader || "").toString().trim();
    let headInner;
    if (customHeader) {
      // fully editable header: {count} & {free} tokens; [text] -> badge
      headInner = esc(customHeader)
        .replace(/\{count\}/gi, GIFTS.length)
        .replace(/\{free\}/gi, "<b>FREE</b>")
        .replace(/\[([^\]]+)\]/g, '<span class="rbx2__badge">$1</span>')
        .replace(/\s{2,}/g, " ").trim();
    } else {
      headInner = `${esc(title)} ${GIFTS.length} <b>FREE</b> gifts`;
      if (monthly) headInner += `, ${monthlyBadge}`;
      if (additional) headInner += ` and an additional ${additionalBadge}`;
      if (monthly || additional) headInner += ` every month!`;
    }
    const head = showHeader ? `<span class="rbx2__gifts-headtxt">${headInner}</span>` : "";
    const cards = GIFTS.map(g => `
      <div class="rbx2__gift">
        ${g.img ? `<img class="rbx2__gift-img" src="${esc(g.img)}" alt="">` : ""}
        <div class="rbx2__gift-txt">
          <div class="rbx2__gift-monthrow"><span class="rbx2__gift-month">${esc(g.month)}</span>${g.badge ? `<span class="rbx2__badge rbx2__badge--gift">${esc(g.badge)}</span>` : ""}</div>
          <div class="rbx2__gift-text">${esc(g.text)}</div>
          <div class="rbx2__gift-price">${g.was ? `<span class="rbx2__gift-was">${esc(g.was)}</span>` : ""}<span class="rbx2__gift-free">${esc(g.right || "FREE")}</span></div>
        </div>
      </div>`).join("");
    const benefits = [COPY.cancelNote, COPY.benefit2, COPY.benefit3].filter(Boolean);
    const benefitsRow = benefits.length
      ? `<div class="rbx2__benefits">` + benefits.map((b, i) => `${i ? '<span class="rbx2__bdot" aria-hidden="true"></span>' : ''}<span class="rbx2__benefit">${esc(b)}</span>`).join("") + `</div>`
      : "";
    return `<div class="rbx2__gifts-box ${giftsOpen ? "" : "is-collapsed"}">
      <button type="button" class="rbx2__gifts-head" data-rbx2-gifts-toggle>${head}<span class="rbx2__gifts-caret">${CARET}</span></button>
      <div class="rbx2__gifts">${cards}</div>
      ${benefitsRow}
    </div>`;
  }

  function deliverSelect(m) {
    if (!m.subOpts.length) return "";
    const options = m.subOpts.map(o => `<option value="${esc(o.id)}" ${o.id === m.curId ? "selected" : ""}>${esc(freqLabel(o.label))}</option>`).join("");
    const current = (m.subOpts.find(o => o.id === m.curId) || m.subOpts[0]).label;
    return `<div class="rbx2__deliver">
      <span class="rbx2__deliver-txt">${esc(COPY.deliveryPrefix || "Deliver every:")} <b>${esc(freqLabel(current))}</b></span>
      <span class="rbx2__deliver-caret">${CARET}</span>
      <select class="rbx2__deliver-native" data-rbx2-deliver aria-label="Delivery frequency">${options}</select>
    </div>`;
  }

  function freqBlock(m) {
    const subActive = m.isSub;
    const subP = priceCache.sub || m.price;
    const oneP = priceCache.otp || m.price;
    const subPct = subPercent(m);
    const subSave = saveAmount(subP.was, subP.now);   // real-time "Save £X" from the sub was/now
    const subPrice = `${subP.was ? `<span class="rbx2__opt-was">${esc(subP.was)}</span>` : ""}<span class="rbx2__opt-now">${esc(subP.now)}</span>`;
    return `
      <p class="rbx2__freq-title">${esc(COPY.freqTitle || "Choose your buying frequency")}</p>
      <div class="rbx2__freq">
        <div class="rbx2__opt ${subActive ? "is-active" : ""}" data-rbx2-plan="sub" role="radio" aria-checked="${subActive}" tabindex="0">
          <div class="rbx2__opt-head">
            <div class="rbx2__opt-left">
              <span class="rbx2__radio"></span>
              <span class="rbx2__opt-label">${esc(COPY.subscribeLabel || "Subscribe")}</span>
              ${subSave ? `<span class="rbx2__badge">Save ${esc(subSave)}</span>` : ""}
            </div>
            <div class="rbx2__opt-price">${subPrice}</div>
          </div>
          ${subActive ? deliverSelect(m) : ""}
          ${subActive ? giftsBox(m) : ""}
        </div>
        <div class="rbx2__opt ${!subActive ? "is-active" : ""}" data-rbx2-plan="one" role="radio" aria-checked="${!subActive}" tabindex="0">
          <div class="rbx2__opt-head">
            <div class="rbx2__opt-left">
              <span class="rbx2__radio"></span>
              <span class="rbx2__opt-label">${esc(COPY.oneTimeLabel || "One-time")}</span>
            </div>
            <div class="rbx2__opt-price"><span class="rbx2__opt-now">${esc(oneP.now)}</span></div>
          </div>
        </div>
      </div>`;
  }

  function atcBlock(m) {
    // only show the "Your Bundle contains:" helper once every step quota is met
    const help = (COPY.summaryTitle && !m.atcDisabled)
      ? `<div class="rbx2__atc-help">${esc(COPY.summaryTitle)} <b>${esc([COPY.summaryPoint1, COPY.summaryPoint2, COPY.summaryPoint3].filter(Boolean).join(" · "))}</b></div>`
      : "";
    return `<div class="rbx2__atc-wrap">
      <button type="button" class="rbx2__atc" data-rbx2-atc data-rbx2-inline ${m.atcDisabled ? "disabled" : ""}>${esc(COPY.atcLabel || "ADD TO CART")}</button>
      ${help}
    </div>`;
  }

  // ---------- Sticky bar (ORIGINAL design, Figma buttons) ----------
  function stickyMarkup(m) {
    // Universal chip logic:
    //  • multi-step bundle  -> one chip PER STEP (like the original bar), on when the step completes
    //  • single-step bundle -> chips follow the QUANTITY selected (summary points)
    const multi = m.steps.length > 1;
    let chips = "", done, total;
    if (multi) {
      const reqSteps = m.steps.filter(s => s.need > 0);
      const steps = reqSteps.length ? reqSteps : m.steps;
      total = steps.length;
      done = steps.filter(s => s.complete).length;
      chips = steps.map(s => `<div class="rbx-chip ${s.complete ? "is-on" : ""}"><span class="rbx-dot">${CHECK}</span><span>${esc(s.title)}</span></div>`).join("");
    } else {
      const need = m.totalNeed || m.totalChosen;
      const sLabels = [COPY.summaryPoint1, COPY.summaryPoint2, COPY.summaryPoint3].filter(Boolean);
      const count = sLabels.length || need || m.totalChosen || 1;
      total = need || count;
      done = m.totalChosen;
      let out = "";
      for (let i = 0; i < count; i++) {
        const label = sLabels[i] || sLabels[sLabels.length - 1] || (m.steps[0]?.title || ("ITEM " + (i + 1)));
        out += `<div class="rbx-chip ${i < m.totalChosen ? "is-on" : ""}"><span class="rbx-dot">${CHECK}</span><span>${esc(label)}</span></div>`;
      }
      chips = out;
    }
    const cur = (m.isSub ? priceCache.sub : priceCache.otp) || m.price;
    const saveAmt = saveAmount(cur.was, cur.now);
    const save = saveAmt ? `Save ${saveAmt}` : "";
    const subPct = subPercent(m);
    // Until the bundle is complete the bar isn't "activated" — both buttons say DISCOVER
    // (tap = scroll up to the builder). Once ready, show the real ATC / Subscribe actions.
    const ready = !m.atcDisabled;
    const actions = ready
      ? `<button type="button" class="rbx-btn2" data-rbx2-atc data-rbx2-plan-set="one">${esc(COPY.atcLabel || "ADD TO CART")}</button>
         <button type="button" class="rbx-btn2 rbx-btn2--sub" data-rbx2-atc data-rbx2-plan-set="sub">${esc(COPY.subscribeCtaPrefix || "SUBSCRIBE")}${subPct && !COPY.hideStickySave ? `<span class="rbx-btn2-save">SAVE -${esc(subPct)}%</span>` : ""}</button>`
      : `<button type="button" class="rbx-btn2 is-discover" data-rbx2-discover>${esc(COPY.atcLabel || "ADD TO CART")}</button>
         <button type="button" class="rbx-btn2 rbx-btn2--sub is-discover" data-rbx2-discover>${esc(COPY.subscribeCtaPrefix || "SUBSCRIBE")}${subPct && !COPY.hideStickySave ? `<span class="rbx-btn2-save">SAVE -${esc(subPct)}%</span>` : ""}</button>`;
    return `<div class="rbx-sticky-inner">
      <div class="rbx-sticky-content">
        <div class="rbx-sticky-left">
          <div class="rbx-sticky-top">
            <div class="rbx-sticky-title">Selected ${done}/${total}</div>
            <div class="rbx-sticky-chips">${chips}</div>
          </div>
          <div class="rbx-sticky-priceRow">
            ${cur.was ? `<span class="rbx-sticky-compare">${esc(cur.was)}</span>` : ""}
            <span class="rbx-sticky-price">${esc(cur.now)}</span>
            ${save ? `<span class="rbx-sticky-badge">${save}</span>` : ""}
          </div>
        </div>
        <div class="rbx-sticky-actions">${actions}</div>
      </div>
    </div>`;
  }

  // ---------- Mount + render ----------
  let UI, STICKY;
  function ensureMount() {
    if (!UI) { UI = document.createElement("div"); UI.className = "rbx2"; applyTokens(UI); CTRL.appendChild(UI); }
    if (!STICKY) { STICKY = document.createElement("div"); STICKY.id = "rbx-sticky-v2"; STICKY.className = "is-hidden"; applyTokens(STICKY); document.body.appendChild(STICKY); }
    const app = qs("#bundling-app", ROOT) || qs(".rb-app", ROOT);
    if (app && !app.classList.contains("rbx2-native-hidden")) app.classList.add("rbx2-native-hidden");
  }

  let _raf = null;
  let _lastChosen = 0;   // # of bundle items chosen (set in render) — gates the mobile sticky bar
  let _lastUIHtml = "", _lastStickyHtml = "";   // skip redundant innerHTML rebuilds (iOS image flicker/loop)

  // Persist decoded <img> nodes across re-renders. Rebuilding UI.innerHTML recreates every
  // element; on iOS Safari/Chrome a fresh <img> re-decodes and visibly flashes (Android/desktop
  // serve from cache instantly, so it only shows on iOS). We render an empty placeholder div and
  // move the SAME cached, already-loaded <img> back into it — moving a live node never refetches,
  // so the product-option images stay rock-steady no matter how often ReCharge makes us re-render.
  const _imgCache = new Map();
  function getImg(src, alt) {
    let img = _imgCache.get(src);
    if (!img) { img = document.createElement("img"); img.loading = "lazy"; img.src = src; _imgCache.set(src, img); }
    img.alt = alt || "";
    return img;
  }
  function hydrateImages(container) {
    qsa(".rbx2__card-img[data-img-src]", container).forEach((ph) => {
      const src = ph.getAttribute("data-img-src");
      ph.removeAttribute("data-img-src");
      if (!src) return;
      const img = getImg(src, ph.getAttribute("data-img-alt"));
      ph.removeAttribute("data-img-alt");
      if (img.parentNode !== ph) ph.appendChild(img);
    });
  }
  function updateStickyVisibility() {
    if (!STICKY) return;
    // mirror the original: hidden while the purchase area (inline ATC) is in view, shown once scrolled away
    const anchor = qs("[data-rbx2-inline]", UI) || qs(".rbx2__atc-wrap", UI) || UI;
    let hidden = isInView(anchor, 0.35);
    // Mobile only: keep the bar hidden until the shopper selects at least one product in the bundle
    if (!hidden && _lastChosen <= 0 && window.matchMedia("(max-width: 749px)").matches) hidden = true;
    STICKY.classList.toggle("is-hidden", hidden);
  }

  function render() {
    const m = readModel();
    _lastChosen = m.totalChosen;   // gate the mobile sticky bar on having a selection
    // keep the configured default subscription selected until the shopper picks one
    const wantPlan = defaultPlanId();
    if (wantPlan && !_userPicked && m.curId !== "0" && m.curId !== wantPlan) { setPlanById(wantPlan); }
    if (activeStep >= m.steps.length) activeStep = 0;
    const step = m.steps[activeStep] || m.steps[0];

    // re-probe both plan prices whenever the selected quantities change
    const sig = m.steps.flatMap(s => s.items).map(i => i.qty).join(",");
    if (!_probing && sig !== _lastSig) { _lastSig = sig; probePrices(); }

    const stepFull = step.need > 0 && step.chosen >= step.need;   // quota met → lock increments
    const cardSubs = [COPY.cardSub1, COPY.cardSub2, COPY.cardSub3];
    const stepSub = (cardSubs[activeStep] || "").trim();          // per-step "30 Pack" / "12 Servings"

    // optional price shown next to the builder heading (selected plan)
    const curP = (m.isSub ? priceCache.sub : priceCache.otp) || m.price;
    const saveP = saveAmount(curP.was, curP.now);
    const buildPrice = (COPY.showBuildPrice && (curP.now || curP.was))
      ? `<div class="rbx2__build-price">${curP.now ? `<span class="rbx2__bp-now">${esc(curP.now)}</span>` : ""}${curP.was ? `<span class="rbx2__bp-was">${esc(curP.was)}</span>` : ""}${saveP ? `<span class="rbx2__bp-save">Save ${esc(saveP)}</span>` : ""}</div>`
      : "";
    const buildHead = (COPY.buildTitle || buildPrice)
      ? `<div class="rbx2__build-row">${COPY.buildTitle ? `<p class="rbx2__build-title">${esc(COPY.buildTitle)}</p>` : "<span></span>"}${buildPrice}</div>`
      : "";

    const uiHtml =
      buildHead +
      `<div class="rbx2__builder">
         ${stepTabs(m)}
         <div class="rbx2__section-head">
           <p class="rbx2__section-title">${esc(step.title)}</p>
           ${step.need ? `<span class="rbx2__counter">(${step.chosen}/${step.need})</span>` : ""}
         </div>
         <div class="rbx2__cards">${step.items.map(it => card(it, stepFull, stepSub)).join("")}</div>
       </div>` +
      freqBlock(m) +
      atcBlock(m);

    // Only touch the DOM when the markup actually changed. ReCharge fires our MutationObserver
    // constantly on iOS (scroll / URL-bar resize), which used to rebuild everything each time
    // and reload the images — the "loop" / flicker the shopper saw. Identical output → no-op.
    if (uiHtml !== _lastUIHtml) {
      _lastUIHtml = uiHtml;
      UI.innerHTML = uiHtml;
      hydrateImages(UI);   // move the cached <img> nodes back in — no refetch, no flash
    }

    const stickyHtml = stickyMarkup(m);
    if (stickyHtml !== _lastStickyHtml) {
      _lastStickyHtml = stickyHtml;
      STICKY.innerHTML = stickyHtml;
    }
    updateStickyVisibility();
    syncExternalPrice(m);
  }

  // Feed the live bundle price into the standalone price_save_badge block
  // (it is server-rendered from the bundle variant price, which is £0 for ReCharge bundles)
  function syncExternalPrice(m) {
    const ext = document.querySelector('[id^="block-price_save_badge"]');
    if (!ext) return;
    const cur = (m.isSub ? priceCache.sub : priceCache.otp) || m.price;
    if (!cur.now) return;
    const elNow = ext.querySelector(".pmd-price-new");
    const elWas = ext.querySelector(".pmd-price-normal");
    const elBadge = ext.querySelector(".pmd-badge");
    if (elNow) elNow.textContent = cur.now;
    if (elWas) elWas.textContent = cur.was || "";
    const s = saveAmount(cur.was, cur.now);
    if (elBadge && s) elBadge.textContent = "Save " + s;
  }
  const scheduleRender = () => { if (_freeze || _probing) return; if (_raf) cancelAnimationFrame(_raf); _raf = requestAnimationFrame(render); };

  // ---------- Events ----------
  function bindEvents() {
    UI.addEventListener("click", (e) => {
      // let the native delivery <select> handle its own clicks — never switch plan / re-render here
      if (e.target.closest("[data-rbx2-deliver]")) return;

      const giftsToggle = e.target.closest("[data-rbx2-gifts-toggle]");
      if (giftsToggle) { giftsOpen = !giftsOpen; render(); return; }

      const m = readModel();
      const step = m.steps[activeStep] || m.steps[0];

      const tab = e.target.closest("[data-rbx2-step]");
      if (tab && !tab.classList.contains("is-disabled")) { activeStep = num(tab.getAttribute("data-rbx2-step")); render(); return; }

      const itemEl = e.target.closest("[data-rbx2-item]");
      if (itemEl) {
        const it = step.items.find(i => i.id === itemEl.getAttribute("data-rbx2-item"));
        if (it) {
          if (e.target.closest("[data-rbx2-plus]")) {
            if (it.oos) return;                                    // out of stock — can't add
            if (step.need > 0 && step.chosen >= step.need) return; // hard cap — never exceed
            fireQty(it.plus);
          } else if (e.target.closest("[data-rbx2-minus]")) fireQty(it.minus);
        }
        return;
      }

      const plan = e.target.closest("[data-rbx2-plan]");
      if (plan) {
        _userPicked = true;
        if (plan.getAttribute("data-rbx2-plan") === "sub") setPlanById((m.subOpts.find(o => o.id === m.curId) || m.subOpts[0])?.id || "0");
        else setPlanById(m.oneTime?.id || "0");
        return;
      }

      if (e.target.closest("[data-rbx2-inline][data-rbx2-atc]")) { fireAtc(); return; }
    });

    // functional delivery dropdown — freeze re-render while it's open so the
    // option list can't be wiped mid-selection ("shows and disappears" bug)
    UI.addEventListener("focusin", (e) => { if (e.target.closest("[data-rbx2-deliver]")) _freeze = true; });
    UI.addEventListener("focusout", (e) => { if (e.target.closest("[data-rbx2-deliver]")) { _freeze = false; scheduleRender(); } });
    UI.addEventListener("change", (e) => {
      const sel = e.target.closest("[data-rbx2-deliver]");
      if (sel) { _userPicked = true; _freeze = false; setPlanById(sel.value); }
    });

    // sticky buttons
    STICKY.addEventListener("click", (e) => {
      const disc = e.target.closest("[data-rbx2-discover]");
      if (disc) { (UI || ROOT).scrollIntoView({ behavior: "smooth", block: "start" }); return; }
      const btn = e.target.closest("[data-rbx2-atc]");
      if (!btn || btn.disabled) return;
      const m = readModel();
      if (btn.getAttribute("data-rbx2-plan-set") === "sub") {
        const chosen = m.curId !== "0" ? m.curId : (m.subOpts[0]?.id || "0");
        if (chosen !== "0") setPlanById(chosen);
      } else {
        setPlanById(m.oneTime?.id || "0");
      }
      setTimeout(fireAtc, 60);
    });
  }

  // ---------- Init ----------
  waitForWidget(() => {
    ensureMount();
    bindEvents();
    applyDefaultPlan();                 // auto-select configured default frequency
    setTimeout(applyDefaultPlan, 400);  // retry as ReCharge's Vue widget settles
    setTimeout(applyDefaultPlan, 1200);
    render();
    const mo = new MutationObserver(scheduleRender);
    mo.observe(ROOT, { subtree: true, childList: true, attributes: true, characterData: true });
    getSelect()?.addEventListener("change", scheduleRender);

    // sticky visibility tracks scroll/resize exactly like the original bar
    let ticking = false;
    const onScroll = () => { if (ticking) return; ticking = true; requestAnimationFrame(() => { ticking = false; updateStickyVisibility(); }); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    console.log("[RBX2] mounted");
  });
})();
